import React from 'react';
import {
  Buildings,
  Eye,
  PencilSimple,
  Trash,
  TrashSimple,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { STATUS_OPTIONS } from './useAgencies';

const StatusBadge = ({ status }) => {
  const cfg = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return <Badge className={cfg.color}>{cfg.label}</Badge>;
};

const ContractBadges = ({ agency }) => (
  <div className="flex gap-1 flex-wrap">
    {agency.has_medicaid && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Medicaid</Badge>}
    {agency.has_embs && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">eMBS</Badge>}
    {agency.has_sandata && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Sandata</Badge>}
    {agency.has_bh && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">BH</Badge>}
  </div>
);

const RowActions = ({ agency, canDelete, canPurge, onView, onEdit, onDelete, onPurge }) => (
  <div className="flex gap-1">
    <Button variant="ghost" size="sm" onClick={() => onView(agency)} data-testid={`view-agency-${agency.id}`}>
      <Eye size={16} />
    </Button>
    <Button variant="ghost" size="sm" onClick={() => onEdit(agency)} data-testid={`edit-agency-${agency.id}`}>
      <PencilSimple size={16} />
    </Button>
    {canDelete && agency.id !== 'demo-agency-000' && (
      <Button variant="ghost" size="sm" onClick={() => onDelete(agency.id)} className="text-red-600" data-testid={`deactivate-agency-${agency.id}`}>
        <Trash size={16} />
      </Button>
    )}
    {canPurge && agency.id !== 'demo-agency-000' && (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onPurge(agency)}
        className="text-rose-700 hover:bg-rose-50 hover:text-rose-900"
        title="Permanently delete agency and ALL associated data"
        data-testid={`purge-agency-${agency.id}`}
      >
        <TrashSimple size={16} weight="fill" />
      </Button>
    )}
  </div>
);

/**
 * Read-only agencies table with view/edit/delete row actions and a friendly
 * empty-state. Loading state delegates to a centered spinner.
 */
const AgenciesTable = ({ agencies, loading, canDelete, canPurge, onView, onEdit, onDelete, onPurge }) => (
  <Card className="border-slate-200">
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : agencies.length === 0 ? (
        <div className="py-12 text-center" data-testid="agency-empty-state">
          <Buildings size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No agencies found</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agency Name</TableHead>
              <TableHead>NPI</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Contracts</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencies.map((agency) => (
              <TableRow key={agency.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{agency.name}</p>
                    <p className="text-xs text-slate-500">
                      {agency.address?.city}, {agency.address?.state}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">{agency.npi}</TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{agency.contact?.name}</p>
                    <p className="text-xs text-slate-500">{agency.contact?.email}</p>
                  </div>
                </TableCell>
                <TableCell><ContractBadges agency={agency} /></TableCell>
                <TableCell><StatusBadge status={agency.status} /></TableCell>
                <TableCell>
                  <RowActions
                    agency={agency}
                    canDelete={canDelete}
                    canPurge={canPurge}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onPurge={onPurge}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default AgenciesTable;
export { StatusBadge };
